import { ref } from 'vue';
import { defineStore } from 'pinia';
import { navigateTo, useNuxtApp } from 'nuxt/app';
import ObjectCache from '~~/common/utils/ObjectCache';
import { useAwRequestTracing } from '~~/common/composables/useAwRequestTracing';
import { setErrorNotification } from '~~/common/utils/helper';
import { isApiError } from '~~/common/utils/apiError';
import { M_DELIVERY_METHOD } from '~~/common/config/Modal';
import { PUSH_REVIEW_PRODUCT } from '~~/common/plugins/aw-analytics';
import { AW_AX_CACHE_DEFAULT, AW_AX_CACHE_SEGMENTATION } from '~~/common/plugins/axios/awAxCache';
import { commitAlcoholAlertModal, commitAlcoholConfirmModal } from '~~/common/utils/alcoholManagement';
import { getCategoryUrl } from '~~/common/composables/useUrls';
import { getLocaleCodeForUrl } from '~~/common/utils/localeCode';
import { MSG_PRODUCT_REDIRECT, setCookieMsg } from '~~/common/server/globalMiddlewareConstants';
import { useModalStore } from '~~/common/stores/modal';
import { useCategoryStore } from '~~/common/stores/category';
import { useConfigStore } from '~~/common/stores/config';
import { useUserStore } from '~~/common/stores/user';
import { useDeliveryStore } from '~~/shop/app/stores/delivery';
import { useAuthenticationStore } from '~~/common/stores/authentication';
import type { DeepPartial, ResponseDataOpStrict, Schema } from '~~/common/types/api';

const VARIANT_PROP = 'selectedVariant';

// FE view-model: the variant plus the parts the store loads from the dedicated
// detail endpoints (`/details/*`, `/stock_infos`, `/uncached/*`) and aggregates here.
export type ProductVariantFe = DeepPartial<Schema<'ProductModel.ProductVariantModel'>> & {
  stockInfos?: DeepPartial<Schema<'ProductModel.ProductStockInfoModel'>>[];
  reviewSum?: DeepPartial<Schema<'ReviewSum'>>;
  reviewable?: string;
  deliveryPrice?: DeepPartial<Schema<'ProductModel.ProductDeliveryPriceModel'>>;
  details?: DeepPartial<Schema<'ProductModel.ProductVariantDetailsModel'>>[] | string[];
  ingredients?: unknown;
  ingredientsDetailed?: DeepPartial<Schema<'IngredientsDetailedModel'>>[];
  nutrition?: DeepPartial<Schema<'ProductNutritionModel'>>;
  allergens?: DeepPartial<Schema<'ProductAllergenModel'>>;
  allergensDetailed?: DeepPartial<Schema<'AllergensDetailedModel'>>[];
};

/**
 * The product `data` state: the generated `ProductModel` (defensively partial)
 * with the selected variant replaced by the FE-extended variant shape and a
 * frontend-only `feMeta` marker. `null` is the "no/failed product" state
 * (the JS version used `false`; the store state now uses `null`, the `fetch*`
 * return values still resolve to `false` on error to preserve their contract).
 */
export type ProductData = Omit<DeepPartial<Schema<'ProductModel'>>, 'categories'> & {
  feMeta?: { isCached: boolean };
  selectedVariant?: ProductVariantFe | null;
  categories?: ProductCategory[];
};

//todo use backend API types after backend API swagger is updated with the correct types
type ProductCategory = Omit<
  DeepPartial<Schema<'CategoryModel-default'>>,
  'children' | 'contentId' | 'contentPosition' | 'productContentId'
> & {
  children?: ProductCategory[];
  contentId?: number[] | null;
  contentPosition?: Record<string, string>[] | null;
  productContentId?: number[] | null;
};

interface FetchByVariant {
  id: number | string;
  variantId: number | string;
}

interface FetchMainProductForProductPagePayload {
  // `loc` comes straight from `route.query.loc`, whose type is
  // `LocationQueryValue | LocationQueryValue[] | undefined`. It is only used for
  // an exact `company.name === loc` string match, so a non-string value simply
  // matches no company (the pre-existing behaviour).
  loc?: string | string[] | null | (string | null)[];
  identifierType: string;
  id: number | string;
}

interface FetchMainProductPayload {
  identifierType: string;
  id: number | string;
  params?: string;
  specialErrorMessage?: unknown;
  disableRedirect?: boolean;
}

interface FetchProductRedirectPayload {
  identifierType: string;
  id: number | string;
}

// The runtime guard in `fetchBundles` validates the wrapper but only checks that `results` is an
// array, so its entries stay defensively typed.
type BundlesResponse = Omit<ResponseDataOpStrict<'get_product_bundles'>, 'results'> & {
  results: DeepPartial<Schema<'ProductModel.ProductBundleModel-default'>>[];
};

interface PushNewReviewPayload {
  id: number | string;
  variantId: number | string;
  variantSku: string;
  rating: number;
  editorName: string;
  body: string;
  anonym: boolean;
  status: string;
}

const productReqCache = new ObjectCache<Promise<ProductData | false>>('productReq', '__');

export class ProductRedirectError extends Error {
  constructor () {
    super('Product redirect');
    this.name = 'ProductRedirectError';
  }
}

export const useProductStore = defineStore('product', () => {
  const nuxtApp = useNuxtApp();
  const configStore = useConfigStore();
  const deliveryStore = useDeliveryStore();
  const categoryStore = useCategoryStore();
  const userStore = useUserStore();
  const modalStore = useModalStore();
  const authenticationStore = useAuthenticationStore();

  const data = ref<ProductData | null>(null);
  const loading = ref(false);

  function setProductLoading (newLoadingValue: boolean) {
    loading.value = newLoadingValue;
  }

  function setProduct (product: ProductData | null) {
    data.value = product;
  }

  function setStockInfos (payload: ProductVariantFe['stockInfos']) {
    const variant = data.value?.[VARIANT_PROP];
    if (variant) {
      variant.stockInfos = payload;
    }
  }

  function setDetails (payload: ProductVariantFe['details']) {
    const variant = data.value?.[VARIANT_PROP];
    if (variant) {
      variant.details = payload;
    }
  }

  function setDeliveryPrice (payload: ProductVariantFe['deliveryPrice']) {
    const variant = data.value?.[VARIANT_PROP];
    if (variant) {
      variant.deliveryPrice = payload;
    }
  }

  function setIngredients (payload: ProductVariantFe['ingredients']) {
    const variant = data.value?.[VARIANT_PROP];
    if (variant) {
      variant.ingredients = payload;
    }
  }

  function setIngredientsDetailed (payload: ProductVariantFe['ingredientsDetailed']) {
    const variant = data.value?.[VARIANT_PROP];
    if (variant) {
      variant.ingredientsDetailed = payload;
    }
  }

  function setNutrition (payload: ProductVariantFe['nutrition']) {
    const variant = data.value?.[VARIANT_PROP];
    if (variant) {
      variant.nutrition = payload;
    }
  }

  function setAllergens (payload: ProductVariantFe['allergens']) {
    const variant = data.value?.[VARIANT_PROP];
    if (variant) {
      variant.allergens = payload;
    }
  }

  function setAllergensDetailed (payload: ProductVariantFe['allergensDetailed']) {
    const variant = data.value?.[VARIANT_PROP];
    if (variant) {
      variant.allergensDetailed = payload;
    }
  }

  function setUncachedProductParts (payload: DeepPartial<Schema<'ProductModel.ProductDetailNonCachedInfoModel'>> | undefined) {
    const variant = data.value?.[VARIANT_PROP];
    if (!variant || !payload) {
      return;
    }
    if ('ageConfirmed' in payload && data.value) {
      data.value.ageConfirmed = payload.ageConfirmed;
    }
    if ('reviewSum' in payload) {
      variant.reviewSum = payload.reviewSum;
    }
    if ('reviewable' in payload) {
      variant.reviewable = payload.reviewable;
    }
    if ('stockInfos' in payload) {
      variant.stockInfos = payload.stockInfos;
    }
    if ('cartInfo' in payload) {
      variant.cartInfo = payload.cartInfo;
    }
    if ('isLoyaltyPriceValid' in payload) {
      variant.isLoyaltyPriceValid = payload.isLoyaltyPriceValid;
    }
    if ('shoppingListsContain' in payload) {
      variant.shoppingListsContain = payload.shoppingListsContain;
    }
    if (data.value?.feMeta?.isCached === true) {
      data.value.feMeta.isCached = false;
    }
  }

  function maybeShowAlcoholModal (product: ProductData | null) {
    const { $awt } = nuxtApp;
    // alcohol product alert when...
    // - adultsOnly is true
    // - not opened delivery modal
    if (product?.adultsOnly && modalStore.activeGenericModal.type !== M_DELIVERY_METHOD) {
      if (userStore.isEcomUser && !product?.ageConfirmed && !userStore.showAlcoholAlertModalBeforeLogIn) {
        commitAlcoholConfirmModal($awt);
      } else if (!userStore.isEcomUser) {
        commitAlcoholAlertModal({ $awt });
      }
    }
  }

  async function fetchMainProductForProductPage ({
    loc,
    identifierType,
    id,
  }: FetchMainProductForProductPagePayload) {
    const {
      $i18n,
    } = nuxtApp;

    // PL store code mapping
    const companies = (configStore.configCommon?.companies || []);
    const companyId = loc !== undefined ? companies.find(c => c.name === loc)?.id : undefined;
    const QP_STORE_CODE = companyId !== undefined ? `company_id=${companyId}` : '';
    const cacheKey = [$i18n.locale.value, deliveryStore.getPostCode, identifierType, String(id), QP_STORE_CODE];
    productReqCache.initCache(cacheKey, () => fetchMainProduct({
      identifierType,
      id,
      params: QP_STORE_CODE,
    }));
    const product = await productReqCache.getCache(cacheKey)?.value;
    productReqCache.deleteCache(cacheKey);
    if (product && product.feMeta?.isCached) {
      await fetchUncachedProductParts({ identifierType, id });
    }
    maybeShowAlcoholModal(data.value);
    return product;
  }

  async function fetchMainProduct ({
    identifierType,
    id,
    params,
    specialErrorMessage,
    disableRedirect = false,
  }: FetchMainProductPayload): Promise<ProductData | false> {
    const {
      $api,
      $logger,
    } = nuxtApp;

    await authenticationStore.reInitToken();

    try {
      setProductLoading(true);
      const categoryIdBeforeVisitProductPage = categoryStore.categoryIdBeforeVisitProductPage;
      let queryString = `${categoryIdBeforeVisitProductPage ? `?category_id=${categoryIdBeforeVisitProductPage}` : ''}`;
      queryString = params ? `${queryString}${queryString ? '&' : '?'}${params}` : queryString;
      const ax = await $api.get<ProductData>(`/products/${identifierType}/${id}${queryString}`, { awAxCache: AW_AX_CACHE_SEGMENTATION });
      const isCached = 'awAxCacheHit' in ax && Boolean(ax.awAxCacheHit);
      const result = ax.data;
      if (result.defaultVariant) {
        delete result.defaultVariant;
      }
      result.feMeta = { isCached };
      setProduct(result);
      return result;
    } catch (error) {
      setProduct(null);
      if (isApiError(error) && error.response?.status === 404 && !disableRedirect) {
        const redirected = await fetchProductRedirect({ identifierType, id });
        if (redirected) {
          throw new ProductRedirectError();
        }
      } else {
        $logger.error(specialErrorMessage ?? error);
      }
      await setErrorNotification({ nuxtApp, error: specialErrorMessage ?? error });
      return false;
    } finally {
      setProductLoading(false);
    }
  }

  async function fetchUncachedProductParts ({ identifierType, id }: FetchProductRedirectPayload) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const payload = await $api.$get<DeepPartial<Schema<'ProductModel.ProductDetailNonCachedInfoModel'>>>(`/products/uncached/${identifierType}/${id}`);
      setUncachedProductParts(payload);
      return payload;
    } catch (error) {
      $logger.error(error);
    }
  }

  async function fetchStockInfos ({
    id,
    variantId,
  }: FetchByVariant) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    const defaultValue: ProductVariantFe['stockInfos'] = [];
    try {
      const response = await (__AW_IS_SHOP__
        ? $api.$get<ProductVariantFe['stockInfos']>(`/products/${id}/variants/${variantId}/stock_infos`)
        : defaultValue
      );
      setStockInfos(response);
    } catch (error) {
      if (!isApiError(error) || error.response?.status !== 404) {
        $logger.error(error);
        await setErrorNotification({ nuxtApp, error });
      }
    } finally {
      setProductLoading(false);
    }
  }

  async function fetchBundles ({
    id,
    variantId,
  }: FetchByVariant): Promise<BundlesResponse> {
    const {
      $api,
      $logger,
    } = nuxtApp;
    const defaultValue: BundlesResponse = {
      itemCount: 0,
      pageCount: 1,
      currentPage: 1,
      results: [],
    };
    try {
      const response: unknown = await (__AW_IS_SHOP__
        ? $api.$get<unknown>(`/products/${id}/variants/${variantId}/bundles?itemsPerPage=100&page=1`)
        : defaultValue
      );
      if (
        typeof response === 'object' &&
          response &&
          'itemCount' in response && typeof response.itemCount === 'number' &&
          'pageCount' in response && typeof response.pageCount === 'number' &&
          'currentPage' in response && typeof response.currentPage === 'number' &&
          'results' in response && Array.isArray(response.results)
      ) {
        return response as BundlesResponse;
      } else {
        throw new TypeError(`Incorrect /bundles response for id: ${id}, variantId: ${variantId}`);
      }
    } catch (error) {
      setProductLoading(false);
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
      return defaultValue;
    }
  }

  async function fetchReviews ({
    id,
    variantId,
  }: FetchByVariant): Promise<DeepPartial<Schema<'ReviewModel'>>[] | undefined> {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      return await (__AW_IS_SHOP__
        ? $api.$get<DeepPartial<Schema<'ReviewModel'>>[]>(`/products/${id}/variants/${variantId}/reviews?page=1`)
        : []
      );
    } catch (error) {
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
    }
  }

  async function fetchDeliveryPrice ({
    id,
    variantId,
  }: FetchByVariant) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const payload = await (__AW_IS_SHOP__
        ? $api.$get<ProductVariantFe['deliveryPrice']>(`/products/${id}/variants/${variantId}/delivery_price`)
        : {}
      );
      setDeliveryPrice(payload);
    } catch (error) {
      setProductLoading(false);
      setProduct(null);
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
    }
  }

  async function fetchDetails ({
    id,
    variantId,
  }: FetchByVariant) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const payload = await $api.$get<ProductVariantFe['details']>(`/products/${id}/variants/${variantId}/details`, { awAxCache: AW_AX_CACHE_DEFAULT });
      setDetails(payload || []);
    } catch (error) {
      setProductLoading(false);
      setProduct(null);
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
    }
  }

  async function fetchDescription ({
    id,
    variantId,
  }: FetchByVariant): Promise<{ description: string }> {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      return await $api.$get<{ description: string }>(`/products/${id}/variants/${variantId}/details/description`, { awAxCache: AW_AX_CACHE_DEFAULT });
    } catch (error) {
      if (!isApiError(error) || error.response?.status !== 404) {
        $logger.error(error);
        await setErrorNotification({ nuxtApp, error });
      }
    }
    return { description: '' };
  }

  async function fetchIngredients ({
    id,
    variantId,
  }: FetchByVariant) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const result = await $api.$get<ProductVariantFe['ingredients']>(`/products/${id}/variants/${variantId}/details/ingredients`, { awAxCache: AW_AX_CACHE_DEFAULT });
      setIngredients(result);
    } catch (error) {
      if (!isApiError(error) || error.response?.status !== 404) {
        setProduct(null);
        $logger.error(error);
        await setErrorNotification({ nuxtApp, error });
      }
    } finally {
      setProductLoading(false);
    }
  }

  async function fetchIngredientsDetailed ({
    id,
    variantId,
  }: FetchByVariant) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const result = await $api.$get<ProductVariantFe['ingredientsDetailed']>(`/products/${id}/variants/${variantId}/details/ingredientsDetailed`, { awAxCache: AW_AX_CACHE_DEFAULT });
      setIngredientsDetailed(result);
    } catch (error) {
      if (!isApiError(error) || error.response?.status !== 404) {
        setProduct(null);
        $logger.error(error);
        await setErrorNotification({ nuxtApp, error });
      }
    } finally {
      setProductLoading(false);
    }
  }

  async function fetchParameterList ({
    id,
    variantId,
  }: FetchByVariant): Promise<{ parameters: DeepPartial<Schema<'ProductParameterModel'>>[] }> {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      return await $api.$get<{ parameters: DeepPartial<Schema<'ProductParameterModel'>>[] }>(`/products/${id}/variants/${variantId}/details/parameterList`, { awAxCache: AW_AX_CACHE_DEFAULT });
    } catch (error) {
      if (!isApiError(error) || error.response?.status !== 404) {
        $logger.error(error);
        await setErrorNotification({ nuxtApp, error });
      }
    }
    return { parameters: [] };
  }

  async function fetchNutrition ({
    id,
    variantId,
  }: FetchByVariant) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const result = await $api.$get<ProductVariantFe['nutrition']>(`/products/${id}/variants/${variantId}/details/nutrition`, { awAxCache: AW_AX_CACHE_DEFAULT });
      setNutrition(result);
    } catch (error) {
      if (!isApiError(error) || error.response?.status !== 404) {
        setProduct(null);
        $logger.error(error);
        await setErrorNotification({ nuxtApp, error });
      }
    } finally {
      setProductLoading(false);
    }
  }

  async function fetchAllergens ({
    id,
    variantId,
  }: FetchByVariant) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const result = await $api.$get<ProductVariantFe['allergens']>(`/products/${id}/variants/${variantId}/details/allergens`, { awAxCache: AW_AX_CACHE_DEFAULT });
      setAllergens(result);
    } catch (error) {
      if (!isApiError(error) || error.response?.status !== 404) {
        setProduct(null);
        $logger.error(error);
        await setErrorNotification({ nuxtApp, error });
      }
    } finally {
      setProductLoading(false);
    }
  }

  async function fetchAllergensDetailed ({
    id,
    variantId,
  }: FetchByVariant) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const result = await $api.$get<ProductVariantFe['allergensDetailed']>(`/products/${id}/variants/${variantId}/details/allergensDetailed`, { awAxCache: AW_AX_CACHE_DEFAULT });
      setAllergensDetailed(result);
    } catch (error) {
      if (!isApiError(error) || error.response?.status !== 404) {
        setProduct(null);
        $logger.error(error);
        await setErrorNotification({ nuxtApp, error });
      }
    } finally {
      setProductLoading(false);
    }
  }

  async function fetchProductCategory ({ categoryId }: { categoryId: number | string }) {
    const { $logger } = nuxtApp;
    try {
      await categoryStore.fetchCategoryById(categoryId);
    } catch (error) {
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
    }
  }

  async function pushNewReview ({
    id,
    variantId,
    variantSku,
    rating,
    editorName,
    body,
    anonym,
    status,
  }: PushNewReviewPayload): Promise<[true, unknown] | [false, unknown]> {
    const {
      $api,
      $logger,
      $awAnalytics,
    } = nuxtApp;
    try {
      const result = await $api.post(`products/${id}/variants/${variantId}/reviews`, {
        rating,
        editorName,
        body,
        anonym,
        status,
      });
      try {
        $awAnalytics[PUSH_REVIEW_PRODUCT]({
          productSku: variantSku,
          numberOfStars: rating,
        });
      } catch (err) {
        $logger.error(err);
      }
      return [true, result];
    } catch (err) {
      $logger.error(err);
      return [false, err];
    }
  }

  async function removeReview ({ reviewId }: { reviewId: number | string }): Promise<[true, unknown] | [false, unknown]> {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      return [true, await $api.$delete<unknown>(`/me/reviews/${reviewId}`)];
    } catch (err) {
      $logger.error(err);
      return [false, err];
    }
  }

  async function fetchProductRedirect ({ identifierType, id }: FetchProductRedirectPayload) {
    const {
      $api,
      $logger,
      $i18n,
    } = nuxtApp;
    try {
      const result = await $api.$get<{ status?: number; category?: number }>(`/products/redirect/${identifierType}/${id}`);
      const redirectStatus = result?.status ?? 302;
      const categoryId = result?.category;
      if (!categoryId || redirectStatus === 404) {
        return false;
      }
      let category = categoryStore.getCategoryFromTreeById({ treeId: categoryId });
      if (!category) {
        await categoryStore.fetchCategories();
        category = categoryStore.getCategoryFromTreeById({ treeId: categoryId });
      }
      if (!category) {
        return false;
      }
      const localeCodeToUrl = `/${getLocaleCodeForUrl({ $i18n })}`.replace(/\/$/, '');
      const categoryUrl = getCategoryUrl(category, localeCodeToUrl);
      await nuxtApp.runWithContext(() => {
        const requestId = useAwRequestTracing().getFeServerRequestId();
        setCookieMsg({
          nuxtApp,
          cb (redirectMsg) {
            redirectMsg[MSG_PRODUCT_REDIRECT] = { requestId };
          },
        });
        return navigateTo(categoryUrl, { redirectCode: redirectStatus });
      });
      return true;
    } catch (error) {
      $logger.error(error);
      return false;
    }
  }

  const isPriceDiscounted = (price: { discounted?: boolean; isDiscounted?: boolean }) => price.discounted || price.isDiscounted;

  return {
    data,
    loading,
    fetchMainProductForProductPage,
    fetchMainProduct,
    fetchUncachedProductParts,
    fetchStockInfos,
    fetchBundles,
    fetchReviews,
    pushNewReview,
    removeReview,
    fetchDeliveryPrice,
    fetchDetails,
    fetchDescription,
    fetchIngredients,
    fetchIngredientsDetailed,
    fetchParameterList,
    fetchNutrition,
    fetchAllergens,
    fetchAllergensDetailed,
    fetchProductCategory,
    isPriceDiscounted,
  };
});
